import { type JSX, useEffect, useState } from "react";

import { useHistory } from "react-router";

import type { Account } from "~/v2/api/rest/account";
import { OnboardingLayout } from "~/v2/components/OnboardingLayout";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { FpslAgreement } from "./FpslAgreement";
import { FpslStart } from "./FpslStart";

export const FPSL_ONBOARDING_STEP = ["start", "agreement"] as const;

export type FPSLStep = (typeof FPSL_ONBOARDING_STEP)[number];

const STEPS: Record<
  FPSLStep,
  {
    title: string;
    description: string;
    component: (props: FPSLOnboardingProps) => JSX.Element;
    step?: number;
    path: string;
    nextStep: FPSLStep | null;
    prevStep: FPSLStep | null;
  }
> = {
  start: {
    title: "Stock Lending Program",
    description:
      "By enabling this program, we may lend out eligible stocks or ETFs in your account. You'll still own them, but you won't have voting rights while the program is active.",
    component: FpslStart,
    step: 1,

    path: Path.ROUTE_FPSL_ONBOARDING_START,
    nextStep: "agreement",
    prevStep: null,
  },
  agreement: {
    title: "Last step",
    description: "Please review and accept the following agreement to finish.",
    component: FpslAgreement,
    step: 2,
    path: Path.ROUTE_FPSL_ONBOARDING_AGREEMENT,
    nextStep: null,
    prevStep: "start",
  },
};

export type FPSLOnboardingState = {
  step: FPSLStep | null;
  isLoading: boolean;
};

export interface FPSLOnboardingProps extends FPSLOnboardingState {
  currentAccount: Account;
  onNext: () => void;
  onBack: () => void;
}

const FPSLOnboarding = () => {
  const history = useHistory();

  const [currentStep, setCurrentStep] = useState<FPSLStep>("start");

  const { currentAccount, isLoading } = useAccountContext({
    liveOnly: true,
  });

  useEffect(() => {
    window?.history?.replaceState(null, "", STEPS[currentStep].path);
  }, [currentStep]);

  const step = STEPS[currentStep];
  const StepComponent = step?.component;

  const onBack = () => {
    if (step?.prevStep) {
      setCurrentStep(step.prevStep);
    } else {
      history.replace(Path.ROUTE_DASHBOARD);
    }
  };

  const onNext = () => {
    if (step?.nextStep) {
      setCurrentStep(step.nextStep);
    }
  };

  const onCancel = () => {
    history.replace(Path.ROUTE_DASHBOARD);
  };

  return (
    <OnboardingLayout
      isLoading={isLoading}
      onCancel={onCancel}
      title={step?.title}
      description={step?.description}
      currentStep={step?.step}
      totalSteps={
        Object.keys(STEPS).filter((key) => STEPS[key as FPSLStep].step).length
      }
    >
      {!currentAccount ? (
        <div className="text-center">
          <p className="mb-4 text-gray-600">
            You need a live trading account to access FPSL onboarding.
          </p>
          <button
            type="button"
            onClick={() => history.push(Path.ROUTE_NEW_ACCOUNT)}
            className="rounded bg-blue-600 px-4 py-2 text-white"
          >
            Create Live Account
          </button>
        </div>
      ) : StepComponent ? (
        <StepComponent
          step={currentStep}
          isLoading={isLoading}
          currentAccount={currentAccount}
          onNext={onNext}
          onBack={onBack}
        />
      ) : (
        <div className="text-center">
          <p className="text-gray-600">Loading...</p>
        </div>
      )}
    </OnboardingLayout>
  );
};

export default FPSLOnboarding;
